import React, { type FC, useState, type PropsWithChildren, useContext, type UIEvent } from 'react';

import { withImage, useCarouselScroll, useSmartlingStrings } from '@lucid/hooks';
import kebabCase from 'lodash/kebabCase';

import Text from '../../text/text';
import Card from '../../card/card';
import Image from '../../image/image';
import Box from '../../box/box';
import QuoteCarouselArrow from './quote-carousel-arrow';
import { type Lucid } from '../../../types/types';
import Heading from '../../heading/heading';
import Link from '../../link/link';
import Quote from '../quote';
import LayoutContext from '../../context/layout-context';

export interface QuoteCardCarouselProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** List of quotes to include in the carousel */
  quotes: Lucid.Directus.Generated.Quote_Carousel_Item[];
  /** Variant for links in quote */
  linkVariant?: Lucid.Theme.Component.Link.VariantKeys | Lucid.Theme.Component.Button.VariantKeys;
  /** Typography variant for the quote text */
  quoteTextVariant?: Lucid.Theme.Component.Typography.Paragraph.VariantKeys;
}

const QuoteCardCarousel: FC<PropsWithChildren<QuoteCardCarouselProps>> = ({ className, quotes, ...rest }) => {
  const { shortCode } = useContext(LayoutContext);
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  const withSmartling = useSmartlingStrings({
    keys: ['mktg_read_more_about'],
    shortCode,
  });
  const singleQuote = quotes.length === 1 ? quotes[0].quote : null;

  const { scrollRef, handleScroll, updateCarousel } = useCarouselScroll({
    minItems: 2,
    numSlides: quotes.length,
    handleUpdateCarousel: setCurrentQuoteIndex,
    itemWidth: 520,
  });

  const increment = (index: number) => (index < quotes.length - 1 ? index + 1 : 0);
  const incrementTwoColumn = (index: number) => (index < quotes.length - 2 ? index + 1 : 0);
  const decrement = (index: number) => (index > 0 ? index - 1 : quotes.length - 1);
  const decrementTwoColumn = (index: number) => (index > 0 ? index - 1 : quotes.length - 2);

  return !singleQuote ? (
    <Box
      {...rest}
      css={theme => ({
        margin: 'auto',
        padding: `${theme.space.s64}px 0`,
        display: 'flex',
        justifyContent: 'center',
        [theme.breakpoints.bp1024.min]: {
          display: 'flex',
          justifyContent: 'space-between',
          gap: theme.space.s16,
          padding: `${theme.space.s80}px 0`,
        },
      })}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
    >
      <QuoteCarouselArrow
        css={theme => [
          {
            display: 'none',
            maxWidth: '64px',
          },
          quotes.length >= 2 && {
            [theme.breakpoints.bp768.min]: {
              display: 'flex',
            },
          },
          quotes.length >= 3
            ? {
                [theme.breakpoints.bp1024.min]: {
                  display: 'flex',
                },
              }
            : {
                [theme.breakpoints.bp1024.min]: {
                  display: 'none',
                },
              },
          {
            [theme.breakpoints.bp1024.min]: {
              display: 'none',
            },
          },
        ]}
        direction="left"
        onClick={() => {
          updateCarousel(decrement(currentQuoteIndex));
        }}
        onKeyUp={() => {
          updateCarousel(decrement(currentQuoteIndex));
        }}
      />
      <QuoteCarouselArrow
        css={theme => [
          {
            display: 'none',
            maxWidth: '64px',
          },
          quotes.length >= 2 && {
            [theme.breakpoints.bp768.min]: {
              display: 'flex',
            },
          },
          quotes.length >= 3
            ? {
                [theme.breakpoints.bp1024.min]: {
                  display: 'flex',
                },
              }
            : {
                [theme.breakpoints.bp1024.min]: {
                  display: 'none',
                },
              },
          {
            [theme.breakpoints.bp1024.max]: {
              display: 'none',
            },
          },
        ]}
        direction="left"
        onClick={() => {
          updateCarousel(decrementTwoColumn(currentQuoteIndex));
        }}
        onKeyUp={() => {
          updateCarousel(decrementTwoColumn(currentQuoteIndex));
        }}
      />
      <Box
        css={theme => ({
          maxWidth: '592px',
          overflow: 'hidden',
          [theme.breakpoints.bp1024.min]: {
            maxWidth: '1160px',
          },
        })}
      >
        <Box
          ref={scrollRef}
          css={theme => [
            {
              overflowX: 'scroll',
              scrollSnapType: 'x mandatory',
              gridAutoFlow: 'column',
              display: 'flex',
              height: 'fit-content',
              columnGap: theme.space.s24,
              zIndex: 1,
              padding: theme.space.s24,
            },
            quotes.length === 2 && {
              [theme.breakpoints.bp1024.min]: {
                overflow: 'hidden',
              },
            },
          ]}
          onScroll={e => {
            handleScroll(e as UIEvent<HTMLDivElement>);
          }}
        >
          {quotes.map((quote, index) => {
            const fullName = quote?.quote?.full_name || '';
            const position = quote?.quote?.translations?.[0]?.position
              ? `, ${quote?.quote?.translations?.[0]?.position}`
              : '';
            const company = quote?.quote?.company ? ` ${quote?.quote?.company}` : '';

            const attribution = `${fullName}${position}${company}`;

            return (
              <Card
                key={`quote-card-${quote.id || kebabCase(quote?.quote?.company)}`}
                css={theme => [
                  {
                    label: 'carouselCard',
                    boxShadow: 'none',
                    minWidth: '100%',
                    minHeight: 'min-content',
                    cursor: 'pointer',
                    transition: 'box-shadow 0.1s ease-in',
                    paddingTop: theme.space.s32,
                    paddingBottom: theme.space.s32,
                    scrollSnapAlign: 'start',
                    scrollSnapStop: 'always',
                    [theme.breakpoints.bp900.min]: {
                      minWidth: '95%',
                    },
                    [theme.breakpoints.bp1024.min]: {
                      minWidth: '49%',
                    },
                  },
                ]}
                onFocus={event => {
                  event.preventDefault();
                  updateCarousel(index);
                }}
                tabIndex={index < currentQuoteIndex ? -1 : 0}
              >
                {quote?.quote?.company_logo && (
                  <Box>
                    <Image
                      css={theme => ({
                        maxHeight: '32px',
                        marginBottom: theme.space.s64,
                        [theme.breakpoints.bp1024.min]: {
                          maxHeight: theme.space.s40,
                        },
                      })}
                      imageAlt=""
                      imageSrc={withImage(quote?.quote?.company_logo) || ''}
                    />
                  </Box>
                )}
                {quote?.quote?.company && !quote?.quote?.company_logo && (
                  <Heading as="h2" css={theme => ({ marginBottom: theme.space.s64 })} variant="headingXL">
                    {quote?.quote?.company}
                  </Heading>
                )}
                <Text css={{ margin: 0 }} id={`quote-${index}-description`} variant="paragraphL400">
                  {quote?.quote?.translations?.[0]?.text}
                </Text>
                <Text
                  css={theme => ({ marginTop: theme.space.s16, marginBottom: theme.space.s16 })}
                  id={`quote-${index}-attribution`}
                  variant="labelS500"
                >
                  {`- ${attribution}`}
                </Text>
                {quote.link?.final_url && (
                  <Box
                    css={theme => ({
                      marginTop: 'auto',
                      height: 0,
                      borderBottom: `1px solid ${theme.colors.coolGrey[20]}`,
                    })}
                  />
                )}
                {quote.link?.final_url && (
                  <Link
                    aria-describedby={
                      quote.quote?.company ? undefined : `quote-${index}-description quote-${index}-attribution`
                    }
                    aria-label={
                      quote?.quote?.company
                        ? `${quote.link.default_link_text} - ${withSmartling('mktg_read_more_about', {
                            title: quote?.quote?.company || '',
                          })}`
                        : undefined
                    }
                    css={theme => ({
                      '& a': {
                        marginTop: 0,
                      },
                      [theme.breakpoints.bp1024.min]: {
                        fontSize: theme.fontSizes.fs20,
                      },
                    })}
                    href={quote.link.final_url}
                    iconAnimation="navigational"
                    variant="primaryAlternate"
                  >
                    {quote.link.default_link_text}
                  </Link>
                )}
              </Card>
            );
          })}
        </Box>
        <Box
          css={theme => ({
            display: 'flex',
            justifyContent: 'center',
            marginTop: theme.space.s40,
            height: theme.space.s40,
            [theme.breakpoints.bp768.min]: {
              display: 'none',
            },
          })}
        >
          <QuoteCarouselArrow
            css={{ display: 'flex' }}
            direction="left"
            onClick={() => {
              updateCarousel(decrement(currentQuoteIndex));
            }}
            onKeyUp={() => {
              updateCarousel(decrement(currentQuoteIndex));
            }}
          />
          <QuoteCarouselArrow
            css={{ display: 'flex' }}
            direction="right"
            onClick={() => {
              updateCarousel(increment(currentQuoteIndex));
            }}
            onKeyUp={() => {
              updateCarousel(increment(currentQuoteIndex));
            }}
          />
        </Box>
      </Box>
      <QuoteCarouselArrow
        css={theme => [
          {
            display: 'none',
            maxWidth: '64px',
          },
          quotes.length >= 2 && {
            [theme.breakpoints.bp768.min]: {
              display: 'flex',
            },
          },
          quotes.length >= 3
            ? {
                [theme.breakpoints.bp1024.min]: {
                  display: 'flex',
                },
              }
            : {
                [theme.breakpoints.bp1024.min]: {
                  display: 'none',
                },
              },
          {
            [theme.breakpoints.bp1024.min]: {
              display: 'none',
            },
          },
        ]}
        direction="right"
        onClick={() => {
          updateCarousel(increment(currentQuoteIndex));
        }}
        onKeyUp={() => {
          updateCarousel(increment(currentQuoteIndex));
        }}
      />
      <QuoteCarouselArrow
        css={theme => [
          {
            display: 'none',
            maxWidth: '64px',
          },
          quotes.length >= 2 && {
            [theme.breakpoints.bp768.min]: {
              display: 'flex',
            },
          },
          quotes.length >= 3
            ? {
                [theme.breakpoints.bp1024.min]: {
                  display: 'flex',
                },
              }
            : {
                [theme.breakpoints.bp1024.min]: {
                  display: 'none',
                },
              },
          {
            [theme.breakpoints.bp1024.max]: {
              display: 'none',
            },
          },
        ]}
        direction="right"
        onClick={() => {
          updateCarousel(incrementTwoColumn(currentQuoteIndex));
        }}
        onKeyUp={() => {
          updateCarousel(incrementTwoColumn(currentQuoteIndex));
        }}
      />
    </Box>
  ) : (
    <Box
      as="section"
      css={theme => ({
        background: theme.colors.coolGrey[0],
        '& > div': {
          padding: `${theme.space.s64}px 0px`,
        },
      })}
    >
      <Quote linkVariant="primaryAlternate" quotes={[singleQuote]} quoteTextVariant="paragraphL300" />
    </Box>
  );
};

export { QuoteCardCarousel };
export default QuoteCardCarousel;
